import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Minimize2 } from 'lucide-react';
import { TAG_TONE } from './dashboardTones';

interface Props {
    title: string;
    icon?: React.ReactNode;
    count?: number;
    /** Mittelteil der Kopfzeile (Legende). Rutscht auf schmalen Schirmen in eine eigene Zeile. */
    meta?: React.ReactNode;
    /** Rechts vor dem Schliessen-Knopf (Ansichtsumschalter). */
    actions?: React.ReactNode;
    onClose: () => void;
    children: React.ReactNode;
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Vollbild fuer ein Diagramm des Cockpits.
 *
 * Per Portal direkt unter <body>, `fixed inset-0`: ueber Seitenleiste
 * (z-60), Handy-Navigation (z-90/91), Modalebene (z-100/110) und
 * Hilfe-Knopf (z-114/115), aber UNTER den globalen Hinweisen der App
 * (z-120/130: Warteschlange, Abmelde-Countdown, neue Version) — die
 * duerfen nie verdeckt werden. Deckender Grund, keine Unschaerfe.
 *
 * Esc schliesst, der Fokus wandert beim Oeffnen auf „Vollbild schliessen"
 * und bleibt im Overlay (Tab laeuft im Kreis). Die Rueckgabe des Fokus an
 * den Ausloeser uebernimmt der Aufrufer, weil nur er den Knopf kennt.
 */
export const ChartFullscreen: React.FC<Props> = ({ title, icon, count, meta, actions, onClose, children }) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const closeRef = useRef<HTMLButtonElement>(null);
    const titleId = useId();

    // Der Handler haengt einmal am Dokument; die aktuelle Schliess-Funktion
    // kommt ueber die Ref, damit ein neues `onClose` je Render ihn nicht neu bindet.
    const onCloseRef = useRef(onClose);
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

    // Hintergrund sperren, solange das Vollbild offen ist.
    useEffect(() => {
        const body = document.body;
        const prev = body.style.overflow;
        body.style.overflow = 'hidden';
        return () => { body.style.overflow = prev; };
    }, []);

    useEffect(() => {
        closeRef.current?.focus({ preventScroll: true });
        const onKey = (e: KeyboardEvent) => {
            const root = rootRef.current;
            if (!root) return;
            const target = e.target as Node | null;
            // Nur reagieren, wenn der Fokus bei uns liegt oder nirgends (body) —
            // ein Dialog darueber (z. B. Fremdzugriff) behaelt sein eigenes Esc.
            const ours = !target || target === document.body || target === document.documentElement || root.contains(target);
            if (!ours) return;
            if (e.key === 'Escape' && !e.repeat && !e.defaultPrevented) {
                e.preventDefault();
                onCloseRef.current();
                return;
            }
            if (e.key === 'Tab') {
                const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(el => el.offsetParent !== null || el === document.activeElement);
                if (items.length === 0) { e.preventDefault(); return; }
                const first = items[0], last = items[items.length - 1];
                const active = document.activeElement;
                if (e.shiftKey && (active === first || !root.contains(active))) { e.preventDefault(); last.focus(); }
                else if (!e.shiftKey && (active === last || !root.contains(active))) { e.preventDefault(); first.focus(); }
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);

    if (typeof document === 'undefined') return null;

    return createPortal(
        <div
            ref={rootRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            className="fixed inset-0 z-[118] bg-white flex flex-col outline-none animate-scale-in"
            style={{
                paddingTop: 'env(safe-area-inset-top, 0px)',
                paddingRight: 'env(safe-area-inset-right, 0px)',
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                paddingLeft: 'env(safe-area-inset-left, 0px)',
            }}
        >
            {/* Kopfzeile. Breit (xl): alles in einer Zeile. Schmaler: Titel und
                Schliessen oben, Umschalter/Legende in einer zweiten Zeile —
                der Schliessen-Knopf bleibt so immer oben rechts. */}
            <div className="shrink-0 border-b border-slate-200 px-3 sm:px-4 py-2.5 flex flex-col gap-2">
                <div className="flex items-center gap-3 sm:gap-4">
                    <div className="min-w-0 flex items-center gap-2 mr-auto">
                        {icon && <span className="text-slate-400 shrink-0 [&>svg]:w-4 [&>svg]:h-4">{icon}</span>}
                        <h2 id={titleId} className="text-sm font-semibold text-slate-800 truncate">{title}</h2>
                        {typeof count === 'number' && (
                            <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md border tabular ${TAG_TONE.slate}`}>{count}</span>
                        )}
                    </div>
                    {meta && <div className="hidden xl:block min-w-0">{meta}</div>}
                    {actions && <div className="hidden sm:flex items-center gap-2 shrink-0">{actions}</div>}
                    <button
                        ref={closeRef}
                        type="button"
                        onClick={onClose}
                        aria-label="Vollbild schließen"
                        title="Vollbild schließen (Esc)"
                        className="inline-flex items-center justify-center gap-1.5 h-8 px-2.5 shrink-0 pointer-coarse:h-11 pointer-coarse:min-w-11 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                    >
                        <Minimize2 className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Vollbild schließen</span>
                    </button>
                </div>
                {(meta || actions) && (
                    <div className={`${meta ? 'xl:hidden' : 'sm:hidden'} flex items-center gap-x-4 gap-y-2 flex-wrap`}>
                        {actions && <div className="sm:hidden flex items-center gap-2">{actions}</div>}
                        {meta && <div className="min-w-0">{meta}</div>}
                    </div>
                )}
            </div>
            <div className="flex-1 min-h-0 flex flex-col">
                {children}
            </div>
        </div>,
        document.body,
    );
};
